import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import SummaryTile from "./SummaryTile";
import paymentportalservice from "../../../services/paymentportalservice";
import helpers from "../../../hooks/HelperFunctions";

// Two-series categorical palette (Revenue, Net Revenue) — validated with
// dataviz's scripts/validate_palette.js for both adjacent CVD separation and
// contrast against a white surface: `node validate_palette.js
// "#2a78d6,#eb6834" --mode light` → all checks pass (worst adjacent CVD ΔE
// 24.7, normal-vision ΔE 33.6). Fixed order, never reassigned by a filter.
const SERIES_COLOR = { revenue: "#2a78d6", netRevenue: "#eb6834" };
const GRID_COLOR = "#e1e0d9";
const AXIS_COLOR = "#898781";

const CARD_SX = {
    p: { xs: 1.75, sm: 2.5 },
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "#fff",
    boxShadow: 0,
};

function formatCompactINR(value) {
    if (!value) return "₹0";
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(value);
}

// Custom tooltip per dataviz's interaction spec: one box listing every series
// at the hovered month, a line-key (not a filled swatch box) ahead of each
// name, and the value as the strong/high-contrast element (value leads,
// label follows — inverse of the legend's own hierarchy).
function TrendTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ bgcolor: "#fff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1.5, p: 1.25, boxShadow: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{label}</Typography>
            <Stack rowGap={0.4}>
                {payload.map((entry) => (
                    <Stack key={entry.dataKey} direction="row" alignItems="center" columnGap={0.75}>
                        <Box sx={{ width: 10, height: 2, bgcolor: entry.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{entry.name}</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, ml: "auto" }}>
                            {helpers.formatNumber(entry.value, true)}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
}

const CURRENT_FY_START = (() => {
    const now = new Date();
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
})();
// Meesha's earliest Financial Year (matches CampaignFilters' own FINANCIAL_YEARS list).
const FY_START_YEARS = Array.from({ length: CURRENT_FY_START - 2022 + 1 }, (_, i) => CURRENT_FY_START - i);

const monthColumns = [
    { field: "monthLabel", headerName: "Month", width: 100 },
    { field: "campaignCount", headerName: "Campaigns", width: 110, type: "number" },
    {
        field: "totalBudget",
        headerName: "Budget",
        width: 140,
        type: "number",
        renderCell: ({ value }) => helpers.formatNumber(value, true),
    },
    {
        field: "totalLimit",
        headerName: "Limit",
        width: 140,
        type: "number",
        renderCell: ({ value }) => helpers.formatNumber(value, true),
    },
    {
        field: "totalRevenue",
        headerName: "Revenue",
        width: 140,
        type: "number",
        renderCell: ({ value }) => helpers.formatNumber(value, true),
    },
    {
        field: "totalNetRevenue",
        headerName: "Net Revenue",
        width: 140,
        type: "number",
        renderCell: ({ row }) => (
            <Typography
                sx={{ fontSize: 13, fontWeight: 600, color: (row?.totalNetRevenue ?? 0) < 0 ? "#DC3545" : "#198754" }}
            >
                {helpers.formatNumber(row?.totalNetRevenue, true)}
            </Typography>
        ),
    },
];

export default function RevenueTrendReportTab() {
    const [fyStartYear, setFyStartYear] = useState(CURRENT_FY_START);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        paymentportalservice
            .FetchRevenueTrend(fyStartYear)
            .then((data) => {
                if (!cancelled) setReport(data);
            })
            .catch((error) => console.error("Error fetching revenue trend:", error))
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fyStartYear]);

    // Refetch keeps the frame: the chart/table hold the previous FY's render
    // at reduced opacity while the next one loads, rather than blanking out.
    const months = useMemo(() => report?.months || [], [report]);
    const rows = useMemo(() => months.map((m, i) => ({ id: i, ...m })), [months]);

    return (
        <Stack rowGap={2.5}>
            <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={2}>
                <SummaryTile label="Total Revenue" value={report?.totalRevenue} icon={<TrendingUpRoundedIcon />} />
                <SummaryTile
                    label="Total Net Revenue"
                    value={report?.totalNetRevenue}
                    icon={<SavingsRoundedIcon />}
                />
                <SummaryTile
                    label="Total Budget"
                    value={report?.totalBudget}
                    icon={<AccountBalanceWalletRoundedIcon />}
                />
            </Stack>

            <Card sx={CARD_SX}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Revenue trend</Typography>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="fy-select-label">Financial Year</InputLabel>
                        <Select
                            labelId="fy-select-label"
                            label="Financial Year"
                            value={fyStartYear}
                            onChange={(e) => setFyStartYear(e.target.value)}
                        >
                            {FY_START_YEARS.map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}-{y + 1}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Box sx={{ width: "100%", height: 320, opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={months} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
                            <XAxis
                                dataKey="monthLabel"
                                tick={{ fontSize: 12, fill: AXIS_COLOR }}
                                axisLine={{ stroke: GRID_COLOR }}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fontSize: 12, fill: AXIS_COLOR }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={formatCompactINR}
                                width={70}
                            />
                            <Tooltip content={<TrendTooltip />} cursor={{ stroke: GRID_COLOR, strokeWidth: 1 }} />
                            <Legend
                                iconType="plainline"
                                wrapperStyle={{ fontSize: 12 }}
                                formatter={(value) => <span style={{ color: "#52514e" }}>{value}</span>}
                            />
                            <Line
                                type="monotone"
                                dataKey="totalRevenue"
                                name="Revenue"
                                stroke={SERIES_COLOR.revenue}
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="totalNetRevenue"
                                name="Net Revenue"
                                stroke={SERIES_COLOR.netRevenue}
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Card>

            {/* Table-view twin of the chart above — every value the chart plots, reachable without hovering. */}
            <Card sx={CARD_SX}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>Month-by-month breakdown</Typography>
                <Box sx={{ width: "100%", opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
                    <DataGrid
                        autoHeight
                        columns={monthColumns}
                        rows={rows}
                        density="compact"
                        showCellVerticalBorder
                        showColumnVerticalBorder
                        hideFooter
                        sx={{ border: "none" }}
                    />
                </Box>
            </Card>
        </Stack>
    );
}
